import { Loader2 } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { cn } from '@/lib/cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

const base =
  'inline-flex items-center justify-center gap-2 rounded-control font-sans font-semibold transition duration-200 select-none active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 motion-reduce:active:scale-100'

const variants: Record<Variant, string> = {
  primary:
    'bg-gradient-to-b from-brand to-brand-strong text-on-brand shadow-button hover:-translate-y-px hover:from-brand-strong hover:to-brand-pressed hover:shadow-button-up active:translate-y-0 active:from-brand-pressed active:to-brand-pressed active:shadow-card motion-reduce:hover:translate-y-0',
  secondary:
    'border border-line-strong bg-surface text-ink shadow-[0_1px_2px_rgb(18_41_28/6%)] hover:-translate-y-px hover:bg-surface-tint hover:shadow-card active:translate-y-0 active:bg-surface-tint-strong motion-reduce:hover:translate-y-0',
  ghost: 'text-brand hover:bg-brand-tint active:bg-brand-tint-strong',
  danger:
    'bg-danger text-on-danger shadow-[inset_0_1px_0_rgb(255_255_255/14%),0_1px_2px_rgb(18_41_28/20%)] hover:brightness-95 active:brightness-90',
}

const sizes: Record<Size, string> = {
  sm: 'min-h-9 px-4 text-sm',
  md: 'min-h-12 px-6 text-[0.95rem]',
  lg: 'min-h-14 px-7 text-base',
}

interface StyleProps {
  variant?: Variant
  size?: Size
  wide?: boolean
}

function classes(
  { variant = 'primary', size = 'md', wide = false }: StyleProps,
  extra?: string,
) {
  return cn(base, variants[variant], sizes[size], wide && 'w-full', extra)
}

interface ButtonProps extends ComponentProps<'button'>, StyleProps {
  busy?: boolean
}

export function Button({
  variant,
  size,
  wide,
  busy = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={classes({ variant, size, wide }, className)}
      {...rest}
    >
      {busy && (
        <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />
      )}
      {children}
    </button>
  )
}

interface ButtonLinkProps extends ComponentProps<typeof Link>, StyleProps {}

export function ButtonLink({
  variant,
  size,
  wide,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link className={classes({ variant, size, wide }, className)} {...rest}>
      {children}
    </Link>
  )
}

interface IconButtonProps extends ComponentProps<'button'> {
  label: string
  children: ReactNode
  variant?: 'outline' | 'soft' | 'bare'
}

const iconVariants = {
  outline:
    'border border-line-strong bg-surface text-ink hover:bg-surface-tint',
  soft: 'bg-surface-tint text-ink hover:bg-surface-tint-strong',
  bare: 'text-ink hover:bg-surface-tint',
}

export function IconButton({
  label,
  variant = 'outline',
  className,
  children,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      className={cn(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-control transition duration-200 select-none active:scale-95 disabled:pointer-events-none disabled:opacity-50 motion-reduce:active:scale-100',
        iconVariants[variant],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
}
